import React, { useState, useRef, useEffect } from "react";

/**
 * Parish Announcements — inline accordion with hybrid data source
 * - Glassmorphic design, amber accents, deep shadow
 * - Automatic badge: "Latest" / "Last week"
 * - Smooth JS-driven height animation
 * - Numbered paragraphs and Parish Priest stamp
 * - Auto-sorted by timestamp (newest first)
 * 
 * DATA SOURCES:
 * - Legacy (hardcoded): announcements before Feb 8, 2026
 * - Google Sheets: announcements from Feb 8, 2026 onward
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

// IMPORTANT: Your Google Sheets ID
const SHEET_ID = "1MVmmzs6rULqdqq2kgGlewrlSVnfMOYvbXs9dU83-jIo";

// API endpoint for fetching data
const SHEET_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=announcements`;

// Cutover date: announcements before this use legacy array, after use Google Sheets
const CUTOVER_DATE = new Date("2026-02-05T23:59:59Z");

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const monthMap = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
};

function parseDayMonthYearFromString(s) {
  const regex = /(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)[,]?\s*(\d{4})?/i;
  const m = s.match(regex);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const monthName = m[2].toLowerCase();
  const year = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
  const month = monthMap[monthName];
  if (month === undefined) return null;
  return new Date(year, month, day);
}

function isSameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function animateHeight(element, expand = true, duration = 420) {
  if (!element) return;
  
  const startHeight = expand ? 0 : element.scrollHeight;
  const targetHeight = expand ? element.scrollHeight : 0;
  
  if (Math.round(startHeight) === Math.round(targetHeight)) return;

  element.style.transition = "none";
  element.style.height = startHeight + "px";
  element.style.overflow = "hidden";
  element.style.opacity = expand ? "0" : "1";

  void element.offsetHeight;

  element.style.transition = `height ${duration}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${Math.round(
    duration * 0.8
  )}ms ease`;
  element.style.height = targetHeight + "px";
  element.style.opacity = expand ? "1" : "0";

  const cleanup = () => {
    element.style.transition = "";
    if (expand) {
      element.style.height = "auto";
      element.style.overflow = "";
      element.style.opacity = "1";
    } else {
      element.style.height = "0px";
      element.style.overflow = "hidden";
      element.style.opacity = "0";
    }
    element.removeEventListener("transitionend", cleanup);
  };
  element.addEventListener("transitionend", cleanup);
}

// ============================================================================
// LEGACY ANNOUNCEMENTS (before Feb 8, 2026)
// ============================================================================

const legacyAnnouncementsRaw = [
  {
    dateTitle: "Sunday 1st February, 2026",
    addedTimestamp: "2026-02-01T00:00:00Z",
    fullText: `Monday, 2nd February is the Feast of the Presentation of the Lord. It is also the Day of Consecrated Life. We pray for all the Religious, especially the religious sisters of our parish. There will be blessing of candles before the 7.00 a.m. Mass followed by procession.

Tuesday, 3rd February is the memorial of St. Blaise. Blessing of throats will be done at the 7.00 a.m. and 7.00 p.m. Mass.

On the occasion of World Marriage Day on Sunday, 8th February 2026, the Family Cell is organising "Ever thine, Ever Mine" for all the married couples of our parish. During the special Mass at 8.30 a.m., there will be renewal of marriage vows and blessing, followed by a short celebration in the Parish Hall. Kindly look up the notice board for details.

On 8th February 2026 we will have our Parish Pastoral Council meeting at 11.30 a.m. Kindly note the change in timing and requesting all our PPC members to attend.

In order to prepare ashes for Ash Wednesday, we request you to please bring the blessed palms of last year and place it in the box which will be kept outside the Church.`,
    glimpse: "Feast of the Presentation of the Lord on 2nd Feb with candle blessing. St. Blaise throat blessing on 3rd Feb. World Marriage Day celebration on 8th Feb, PPC meeting timing change, and blessed palms requested for Ash Wednesday."
  },

  {
    dateTitle: "Sunday 25th January, 2026",
    addedTimestamp: "2026-01-25T00:00:00Z",
    fullText: `We celebrate our Parish Feast today, Sunday 25th January with the Feast Mass at 9.00 a.m. There will be no 8.30 a.m. and 10.00 a.m. Mass that day. However, the evening Mass at 4.00 p.m. in Hindi and 6.00 p.m. in English will be celebrated as usual. After the 9.00 a.m. Mass, there will be games and food stalls for everyone to celebrate our parish feast. Kindly refer to the notice board for further details.

On 26th January 2026 we celebrate Republic Day after the 7.00 a.m. Mass in the basement. There will be flag hoisting followed by a programme by the students of St. Stephen's School.

St. Stephen's youth will have a meeting on 1st February 2026 after the 10.00 a.m. Mass. Fr. Omar Fernandes will address our youth along with a resource person from an NGO. Requesting all the youth and present confirmation students to attend.

On 1st February 2026 we celebrate World Day of Consecrated Life. We will have a Mass at 6.00 p.m. followed by fellowship for our religious sisters in the parish hall.

On 8th February 2026 we will have our Parish Pastoral Council meeting after the 10.00 a.m. Mass. Kindly note the change in date and timing.`,
    glimpse: "Parish Feast celebrated today with Feast Mass at 9 a.m. Republic Day programme on 26th Jan. Youth meeting on 1st Feb, World Day of Consecrated Life celebration, and PPC meeting on 8th Feb announced."
  },

  {
    dateTitle: "Sunday 18th January, 2026",
    addedTimestamp: "2026-01-18T00:00:00Z",
    fullText: `Since we are having the Holy Hour on 31st December 2025, the First Friday Holy Hour on 2nd January 2026 will be a prayer service.
On Wednesday, 31st December (New Year's Eve), we will have Holy Hour of Thanksgiving at 9.00 p.m. followed by Mass at 10.00 p.m.
Thursday, 1st January 2026 is the Solemnity of Mary, Mother of God. Masses will be at 7.00 a.m., 9.00 a.m. and 11.00 a.m.
The Parish Office will remain closed on 1st and 2nd January 2026.`,
    glimpse: 'First Friday prayer service on 2nd January. New Year\'s Eve Holy Hour at 9 p.m. Solemnity of Mary on 1st January. Parish Office closed 1st-2nd January.'
  },

  {
    dateTitle: "Sunday 11th January, 2026",
    addedTimestamp: "2026-01-11T00:00:00Z",
    fullText: `Sunday, 11th January 2026 is the Feast of the Baptism of the Lord.
The Parish Pastoral Council meeting will be held today immediately after the 10.00 a.m. Mass.
As a spiritual preparation for our Parish Feast on Sunday, 25th January 2026, a triduum will be held from Thursday, 22nd January to Saturday, 24th January at the 7.00 p.m. Mass.
The Feast Mass on Sunday, 25th January will be celebrated at 9.00 a.m. only. There will be no 8.30 a.m. or 10.00 a.m. Mass.
After the Masses during the triduum, games and food stalls will be organised.`,
    glimpse: 'Feast of the Baptism of the Lord today. PPC meeting after 10 a.m. Mass. Parish Feast triduum from 22nd-24th January announced.'
  },

  {
    dateTitle: "Sunday 4th January, 2026",
    addedTimestamp: "2026-01-04T00:00:00Z",
    fullText: `Sunday, 11th January 2026 is the Feast of the Baptism of the Lord.

The Parish Pastoral Council meeting will be held today (Sunday, 4th January 2026) immediately after the 10.00 a.m. Mass. All members are requested to be present for this meeting.

As a spiritual preparation for our Parish Feast to be celebrated on Sunday, 25th January 2026, a triduum will be held from Thursday, 22nd January to Saturday, 24th January at the 7.00 p.m. Mass. A guest priest will celebrate and preach at the triduum Masses.

The Feast Mass on Sunday, 25th January will be celebrated at 9.00 a.m. only. There will be no 8.30 a.m. or 10.00 a.m. Mass on that day.

After the Masses during the triduum, games and food stalls will be organised. Parishioners are encouraged to kindly patronize and participate.

A raffle draw has also been organised. Raffle tickets will be available near the Book Stall or through your coordinators.`,
    glimpse: 'PPC meeting today after 10 a.m. Mass, Feast of the Baptism of the Lord on 11th January, and Parish Feast triduum and feast schedule announced.'
  },

  {
    dateTitle: "Sunday 28th December, 2025",
    addedTimestamp: "2025-12-28T00:00:00Z",
    fullText: `Since we are having the Holy Hour on 31st December 2025, the First Friday Holy Hour i.e. on 2nd January 2026 will be a prayer service which will be held on the ground floor chapel.

The judges for the Crib Contest will visit your respective cribs on Monday, 29th December around 7.00 p.m. onwards. Those who have participated in the contest are kindly requested to be present at the crib.

On Wednesday, 31st December (New Year's Eve), we will have Holy Hour of Thanksgiving at 9.00 p.m. followed by Mass at 10.00 p.m.

Thursday, 1st January 2026 is the Solemnity of Mary, Mother of God and also New Year's Day. Masses will be at 7.00 a.m. (Konkani), 9.00 a.m. (English) and 11.00 a.m. (Hindi). There will be no evening Mass.

The Parish Pastoral Council meeting will be held on Sunday, 4th January 2026 immediately after the 10.00 a.m. Mass. All members are requested to be present for this meeting.

The Parish Office will remain closed on Thursday, 1st January and Friday, 2nd January 2026.`,
    glimpse:'Holy Hour on 31st December, New Years Eve Mass timings, Solemnity of Mary on 1st January, PPC meeting on 4th January, and Parish Office closure announced.'
  },

  {
    dateTitle: "Sunday 21st December, 2025",
    addedTimestamp: "2025-12-21T00:00:00Z",
    fullText: `Saint Stephen's Church, located at 1, L. Jagmohandas Marg, Cumballa Hill, Mumbai – 400036, announces the notices for the week of Sunday, 11th January, 2026. St Stephen's Youth will be hosting their Youth Café on Sunday, 11th January, after the 7.00 am, 8.30 am, and 10.00 am Masses, and parishioners are kindly requested to patronise and encourage the youth.

As a spiritual preparation for the Parish Feast to be celebrated on Sunday, 25th January, 2026, a triduum will be held from Thursday, 22nd January to Saturday, 24th January during the 7.00 pm Mass. A guest priest will celebrate and preach at these triduum Masses. On Sunday, 25th January, the Feast Mass will be celebrated at 9.00 am only, and there will be no 8.30 am or 10.00 am Masses on that day. After the Masses during the triduum, games and food stalls will be organised in the basement, and parishioners are encouraged to participate. A raffle draw has also been organised, and raffle tickets are available near the book stall or through the coordinators.

The Andrean Cultural Council will be organising a three-day cultural festival on 6th, 7th, and 8th February, 2026 at the St Andrew's High School grounds in Bandra. Parishioners are requested to look up the notice board for further details regarding the event.`,
    glimpse: 'youth café on 11th January, triduum from 22nd-24th January, and parish feast on 25th January announced.'
  },

  {
    dateTitle: "Sunday 14th December, 2025",
    addedTimestamp: "2025-12-14T00:00:00Z",
    fullText: `St. Stephen Youth will have their Youth Café stall on Sunday, 14th December 2025 after the 7.00 a.m., 8.30 a.m. and 10.00 a.m. Masses. Requesting all parishioners to kindly patronize and encourage our youth.

First Holy Communion will be administered to our children on Sunday, 21st December 2025 at the 10.00 a.m. Mass. We keep them in our prayers.

Confessions in preparation for Christmas will be held on Wednesday, 17th December and Thursday, 18th December 2025 from 6.00 p.m. to 8.00 p.m.

The Christmas Eve service on 24th December will begin with Carol singing at 9.30 p.m. followed by Mass at 10.00 p.m. The Masses on Christmas Day will be at 7.00 a.m. (Konkani), 9.00 a.m. (English) and 11.00 a.m. (Hindi). There will be no evening Mass on Christmas Day.

Christmas hampers worth Rs. 3,000/- each will be provided to the needy families of our parish. Those who would like to make a contribution towards the same can give a cheque in the name of St. Stephen Church, clearly stating on the reverse of the cheque that it is for the Christmas hamper.

The last date for registration for the Family Crib contest is Sunday, 14th December 2025. Registration forms are available at the Book Stall or in the Parish Office.

The Cathedral Choir will have an evening of Christmas music on Saturday, 20th December at 7.30 p.m. at Holy Name Cathedral. Entry is open to all.

Take Charge mentorship is now open to youth aged 18 to 24 years. Each selected young adult will receive one-to-one mentoring from a senior professional along with workshops and developmental programs to help them grow in confidence, skills and career clarity. The last date for registration is 28th February 2026. Kindly look up the notice board for details.

SCC Coordinators are requested to collect the Parish calendars from the Parish Office during office hours and distribute them to the families before the New Year begins.`,
    glimpse: "Youth Café today after morning Masses. Christmas confessions, Christmas Eve and Christmas Day Mass timings, Family Crib contest deadline, and youth mentorship programme announced."
  },

  {
    dateTitle: "Sunday 7th December, 2025",
    addedTimestamp: "2025-12-07T00:00:00Z",
    fullText: `Today we will have the Parish Pastoral Council meeting immediately after the 10.00 a.m. Mass. Requesting all the PPC members to attend this meeting.

First Holy Communion will be administered to our children on Sunday, 21st December 2025 at the 10.00 a.m. Mass. We keep them in our prayers.

Confessions in preparation for Christmas will be held on Wednesday, 17th December and Thursday, 18th December 2025 from 6.00 p.m. to 8.00 p.m.

Christmas hampers worth Rs. 3,000/- each will be provided to the needy families of our parish. Those who would like to make a contribution towards the same can give a cheque in the name of St. Stephen Church, clearly stating that it is for the Christmas hampers.

Registration forms for the Family Crib contest are available in the Parish Office or at the Book Stall. The last date for registration is Sunday, 14th December 2025.

The Centre for Social Action will be conducting their annual Advent campaign for hunger and disease. Envelopes for the same will be distributed after every Mass.`,
    glimpse: "PPC meeting today after 10 a.m. Mass. Christmas confessions, First Holy Communion on 21st December, and Family Crib contest registrations announced."
  },

  {
    dateTitle: "Sunday 30th November, 2025",
    addedTimestamp: "2025-11-30T00:00:00Z",
    fullText: `Today, Sunday 30th November 2025, the youth of our parish will be conducting a workshop on Interpersonal Skills after the 10.00 a.m. Mass. Requesting all PPC members, coordinators, animators, heads of associations and their members to attend and encourage our youth.

The Parish Pastoral Council meeting will be held on Sunday, 7th December immediately after the 10.00 a.m. Mass. All PPC members are requested to attend this meeting.

Each community will organise an Advent Prayer Service in their respective communities. We encourage all members to participate so that we as a parish may celebrate Christmas meaningfully.

Christmas hampers worth Rs. 3,000/- each will be provided to the needy families of our parish. Those who would like to make a contribution towards the same can give a cheque in the name of St. Stephen Church, clearly stating that it is for the Christmas hamper.

This year, we have organised a Family and Community Crib competition and invite all parishioners to take part. Registration forms are available in the Parish Office or at the Book Stall.`,
    glimpse: "Youth workshop today after 10 a.m. Mass. Advent prayer services begin in communities and Family & Community Crib competition announced."
  },

  {
    dateTitle: "Sunday 23rd November, 2025",
    addedTimestamp: "2025-11-23T00:00:00Z",
    fullText: `As we celebrate the Solemnity of Our Lord Jesus Christ, the King of the Universe, we invite all our parishioners to spend time in prayer and adoration before the Eucharistic Lord and to join in the procession after the 5.00 p.m. Mass. Kindly refer to the community-wise adoration schedule displayed on the notice board. Please note that there will be no 4.00 p.m. and 6.00 p.m. Mass on this day.

In preparation for Christmas, we invite our parishioners to assist in the cleaning of the Church on Saturday, 29th November 2025 after the 7.00 a.m. Mass. Those willing to render their service are requested to give their names to the ushers.

The Youth of St. Stephen's will be conducting an Interpersonal Skills Workshop on Sunday, 30th November 2025 after the 10.00 a.m. Mass in the Parish Hall. Kindly participate and encourage our youth.

We begin the holy season of Advent from next Sunday, 30th November 2025. Every community will be conducting an Advent Prayer Service in their respective communities. We request all parishioners to participate so that we may prepare ourselves spiritually to celebrate Christmas meaningfully.

The Parish Pastoral Council meeting will be held on Sunday, 7th December immediately after the 10.00 a.m. Mass. All PPC members are requested to attend.`,
    glimpse: "Solemnity of Christ the King celebrated today with adoration and procession. Advent begins next Sunday and PPC meeting announced for 7th December."
  },

  {
    dateTitle: "Sunday 16th November, 2025",
    addedTimestamp: "2025-11-16T00:00:00Z",
    fullText: `Next Sunday, 23rd November, 2025 we celebrate the Solemnity of Our Lord Jesus Christ, the King of the Universe. After the 10.00 a.m. Mass till 4.00 p.m., there will be adoration animated by different communities. At 4.00 p.m. we invite all our parishioners to an hour of adoration followed by Mass at 5.00 p.m. celebrated by Fr. Walter D'Souza. This Mass will be followed by a procession with the Blessed Sacrament to Villa Theresa School, followed by Homily and Benediction by Fr. Walter. Please participate actively in the Holy Hours, Eucharist and Procession. The schedule for Community wise adoration is as follows:

11.00 a.m.  12.00 noon – Sunday School, Confirmation and Youth
12.00 noon – 1.00 p.m. – Romans consisting of Communities 3 & 7
1.00 p.m. – 2.00 p.m. – Galatians consisting of Communities 5 & 8
2.00 p.m. – 3.00 p.m. – Ephesians consisting of Communities 2 & 4
3.00 p.m. – 4.00 p.m. – Corinthians consisting of Communities 1 & 6
4.00 p.m. – 5.00 p.m. – Holy Hour for the entire parish including religious sisters
5.00 p.m. – Holy Eucharist celebrated by Fr. Walter D'Souza followed by procession.

Please note, there will be no 4.00 p.m. & 6.00 p.m. Mass next Sunday.

Christmas hamper worth Rs 3,000/- each will be provided to the needy families of our parish. Those who would like to make a contribution towards the same can give a cheque in favour of St Stephen Church clearly stating that it is for the Christmas hamper.`,
    glimpse: "Next Sunday is the Solemnity of Christ the King with adoration from 10 a.m. to 4 p.m. followed by 5 p.m. Mass and procession. No 4 p.m. and 6 p.m. Mass next Sunday."
  },

  {
    dateTitle: "Sunday 9th November, 2025",
    addedTimestamp: "2025-11-09T00:00:00Z",
    fullText: `St. Stephen Youth will be hosting their Youth Café today after the 7.00 a.m., 8.30 a.m. and 10.00 a.m. Masses. Requesting our parishioners to kindly patronize and motivate our youth.

On Sunday, 23rd November 2025, we celebrate the Solemnity of Our Lord Jesus Christ, the King of the Universe. After the 10.00 a.m. Mass till 4.00 p.m., there will be adoration animated by different communities. At 4.00 p.m., there will be an hour of adoration followed by Mass at 5.00 p.m. celebrated by Fr. Walter D'Souza, followed by a procession with the Blessed Sacrament to Villa Theresa School, Homily and Benediction. Parishioners are encouraged to actively participate in the Holy Hours, Eucharist and the procession.

Please note that there will be no 4.00 p.m. and 6.00 p.m. Mass on that Sunday.

In preparation for Christmas, parishioners are invited to assist in the cleaning of the Church on Saturday, 29th November 2025 after the 7.00 a.m. Mass. Those willing to help are requested to assemble in the basement of the Church.

Online registrations for KG admissions to Villa Theresa High School for the academic year 2026–27 will commence from Monday, 1st November and continue till 18th December 2025. Kindly refer to the notice board for further details.`,
    glimpse: "Youth Café today after morning Masses. Christ the King celebrations on 23rd November and Christmas preparation activities announced."
  },

  {
    dateTitle: "Sunday 2nd November, 2025",
    addedTimestamp: "2025-11-02T00:00:00Z",
    fullText: `Parish Pastoral Council meeting will be held today (tomorrow) Sunday, 2nd November 2025 immediately after the 10.00 a.m. Mass. All PPC members are requested to attend this meeting.

Sunday School re-opens from on Sunday, 02nd November 2025. Sunday School children along with their parents are requested to sit in their allotted places while participating at the 8.30 a.m. family Mass.

07th November 2025 is the First Friday of the month. Holy Hour soon after the 7 p.m. Mass in the church.

All the SCC co-ordinators and animators are requested to attend the SCC steering committee meeting on Sunday 09th Nov 2025 at 11.00 a.m. in the Parish Hall.

Registration for K.G admissions to Villa Theresa High School for the academic year 2026–2027 will commence online from Saturday 1st November 2025 to 18th November 2025. For further information kindly refer to the notice board for details.

Registration for K.G admissions to Convent of Jesus and Mary for the academic year 2026–2027 will be conducted online. Kindly refer to the notice board for details.

Fr Agnel Ashram is celebrating the 98th Death Anniversary of Fr Agnel. Novena in honour begins from 11th November to 19th November. Kindly refer to the notice board.`,
    glimpse: "Parish Pastoral Council meeting on Sunday, 2nd November 2025 after 10 a.m. Mass. Sunday School re-opens today with children sitting in allotted places at 8.30 a.m. Mass."
  },

  {
    dateTitle: "Sunday 26th October, 2025",
    addedTimestamp: "2025-10-26T00:00:00Z",
    fullText: `Saturday, 1st November 2025 is the Solemnity of All Saints. Masses on that day are 7:00 a.m. (Morning) and 7:00 p.m. (Evening).

Sunday, 2nd November 2025 is commemoration of all the Faithful Departed (All Souls Day). Masses will be as usual on Sundays – 7:00 a.m. (in Konkani) 8:30 a.m. & 10:00 a.m. (in English) and at 4:00 p.m. (in Hindi) and at 6:00 p.m. (in English).

Parish Pastoral Council Meeting will be held on Sunday, 2nd November 2025 immediately after the 10:00 a.m. Mass. All the PPC members are requested to attend this meeting.

Sunday School will resume on 2nd November, 2025. Requesting all the parents to send your children for Sunday School.

7th November, 2025 is the First Friday of the month and there will be Holy Hour in the Church immediately after the 7:00 p.m. Mass.

The Parish Fathers express their gratitude to the Sunday School Children, Catechists, Youth, Parents, Co-ordinators & Animators of the SCC Communities, the Religious Sisters of all the three communities and you our dear parishioners for your generosity and support to the Missions by putting up stall and patronizing the same.`,
    glimpse: "Saturday, 1st November 2025 is the Solemnity of All Saints. Masses at 7:00 a.m. and 7:00 p.m. Sunday, 2nd November is All Souls Day with Masses as usual."
  },

  {
    dateTitle: "Sunday 19th October, 2025",
    addedTimestamp: "2025-10-19T00:00:00Z",
    fullText: `Sunday, 19th October 2025 is celebrated as Mission Sunday and a second collection will be made which is for the missions. After all the Sunday morning Masses, the SCC communities have setup stalls selling variety of food items. The proceeds from these earmarked for the Missions will be sent to the Archdiocese. Please patronize and support the missions (No parking will be allowed on Sunday morning).

There will be no Sunday School on 26th October 2025 due to the Diwali Vacations.

Saturday, 1st November 2025 is the Solemnity of All Saints. Masses on that day are at 7:00 a.m. (morning) and 7:00 p.m. (evening).

Sunday, 2nd November 2025 is Commemoration of all the Faithful Departed (All Souls Day). Masses as of every Sunday. A box earmarked for All Souls Day intentions is kept in the Church. Those who wish to offer a Mass on 2nd November (All Souls Day) are requested to write the names of your deceased loved ones and drop it in that box, with or without Mass stipend.`,
    glimpse: "Sunday, 19th October 2025 is celebrated as Mission Sunday. SCC communities will have food stalls after Sunday morning Masses. Proceeds go to the Missions."
  },

  {
    dateTitle: "Sunday 12th October, 2025",
    addedTimestamp: "2025-10-12T00:00:00Z",
    fullText: `Sunday, 12th October 2025, St. Stephen's Youth Group is putting up a snacks & tea stall after the 8.30 a.m. & 10.00 a.m. Masses, the proceeds of the sale go to the Pontifical Mission Society. Please patronize & support our Youth.

Next Sunday, 19th October 2025 will be celebrated as Mission Sunday and a second collection will be made which is for the missions. On that day, the SCC communities will setup stalls selling variety of food stuff. The proceeds from these earmarked towards the Pontifical Mission Society will be sent to the Archdiocese. Please patronize and support the missions (Kindly note no parking will be allowed that morning).

A box earmarked for All Souls Day intentions is kept in the Church. Those who wish to offer a Mass on 2nd November (All Souls Day) are requested to write the names of your deceased loved ones and drop it in that box, with or without a Mass stipend.

The Parish Fathers will be visiting Community No 2 on Monday after the 7.00pm mass. Your respective Community Co-ordinator will inform you of the same.

The CCBI Commission for Theology and Doctrine is organising a National Symposium on "Building a Synodal Church in India" Kindly look up the notice board for details.

At the PPC meeting last Sunday we discussed about how our parishioners could avail of the various Government Schemes entitled to minorities. The social worker at St Elizabeth Hospital and the Community Co-ordinators who have received a what's up message with reference to documents required for the same, will coordinate this process. Kindly get in touch with your respective community coordinators.

As discussed at the last PPC meeting henceforth the mass booking for every month is the LAST SATURDAY of every month, and not on 23rd of every month.`,
    glimpse: "Sunday, 12th October 2025, St. Stephen's Youth Group is putting up a snacks & tea stall after the 8.30 a.m. & 10.00 a.m. Masses. Proceeds go to the Pontifical Mission Society."
  },

  {
    dateTitle: "Sunday 5th October, 2025",
    addedTimestamp: "2025-10-05T00:00:00Z",
    fullText: `Sunday, 5th October we will have our Parish Pastoral Council meeting after the 10 am mass in the Conference Room on the floor. Requesting all PPC members to attend.

Sunday, 5th October is celebrated as Thanksgiving Sunday. As thanksgiving to God and preparation for Mission Sunday celebrated on October, the Sunday School has put up stalls between the a.m. & a.m. Masses. These proceeds earmarked for the Missions will be sent to the Archdiocese. Please encourage each other by patronizing these stalls.

A box earmarked for All Souls Day intentions is kept in the Church. Those who wish to offer a Mass on November (All Souls Day) are requested to write the names of your deceased loved ones and drop it in that box, with or without a Mass stipend.

The Parish fathers express their blessed gratitude to the many parishioners who have joyfully accepted the statue of our Blessed Mother in their homes. The images posted on the Community WhatsApp groups are very heart warming. We pray that Our Blessed Mother queen of every family intercede for us.

St. Stephen's Youth Group is putting up a snacks & tea stall next Sunday, October after the a.m. & a.m. Masses, in order to raise funds for the activities of the Youth Group which include Formation, Fellowship and Service. Please patronize & support our Youth.`,
    glimpse: "Sunday, 5th October we will have our Parish Pastoral Council meeting after the 10 am Mass. All PPC members are requested to attend."
  },

  {
    dateTitle: "Sunday 28th September, 2025",
    addedTimestamp: "2025-09-28T00:00:00Z",
    fullText: `Sunday, 28th September 2025 is celebrated as World Day of Migrants.

The Statue of the Blessed Virgin Mary to be venerated in the 8 communities during the month of October will be blessed on Wednesday, 1st October 2025 immediately after the 7.00 p.m. Mass. Requesting the SCC Co-ordinators to be present for this Mass.

On 3rd October 2025 being the First Friday of the month, we will have our Holy Hour after the 7.00pm mass.

Villa Teresa Convent and the Hostel are celebrating their Centenary on Sunday, 5th October 2025. Requesting you my dear parishioners to join in the eucharistic celebration at 10.00 am in giving thanks to God for their presence in our parish. On this occasion we also pray for vocations to religious life.

Sunday, 5th October 2025 is celebrated as Thanksgiving Sunday in the Archdiocese of Bombay. As thanksgiving to God and preparation for Mission Sunday to be celebrated on 19th October 2025, the Sunday School Children will be putting up stalls next Sunday 5th October 2025. These proceeds earmarked for the Missions will be sent to the Archdiocese. Please encourage each other by patronizing these stalls.

Starting from Sunday, 5th October 2025, a box earmarked for All Souls Day intentions will be kept in the Church. Those who wish to offer a Mass on 2nd November (All Souls Day) are requested to write the names of your deceased loved ones and drop it in that box, with or without a Mass stipend.

Next Sunday 5th October 2025 we will have our PPC meeting after the 10 am mass. Requesting all the PPC members to attend.

St. Stephen's Migrants Association celebrated "Karam festival" last Sunday and it was a great success with more than 800 people participating in the Holy Mass and the subsequent cultural programme. We thank all those who supported us in this endeavour.

We are pleased to inform you of the launch of our parish website. The details of the same are on the notice board.`,
    glimpse: "Sunday, 28th September 2025 is celebrated as World Day of Migrants."
  },

  {
    dateTitle: "Sunday 21st September, 2025",
    addedTimestamp: "2025-09-21T00:00:00Z",
    fullText: `As we have been praying the prayer for Peace, Protection and Communal Harmony in India every Sunday at mass. It is only fitting that we make it part of our daily family prayer, your respective community coordinators will give each family a prayer card, requesting you to kindly pray so that all those framing laws will be filled with wisdom and knowledge for the betterment of every citizen.

The priest of the parish will be visiting Community no 5 on Monday 22nd September 2025 after the 7.00 pm mass. Your respective community coordinator will inform you of the same.`,
    glimpse: "Please include the daily prayer for Peace, Protection and Communal Harmony in your family prayer. Community coordinators will provide prayer cards."
  }
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Announcements() {
  const [openId, setOpenId] = useState(null);
  const [sheetAnnouncements, setSheetAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const panels = useRef({});

  // Fetch Google Sheets data on mount
  useEffect(() => {
    // Only fetch if SHEET_ID is configured
    if (SHEET_ID === "YOUR_GOOGLE_SHEETS_ID_HERE") {
      console.warn("Google Sheets ID not configured. Using legacy data only.");
      setIsLoading(false);
      return;
    }

    console.log("Fetching from:", SHEET_URL);
    fetch(SHEET_URL)
      .then(res => res.text())
      .then(text => {
        console.log("Raw response:", text.substring(0, 200));
        // Google Sheets JSON response is wrapped, need to unwrap it
        const json = JSON.parse(text.substring(47).slice(0, -2));
        console.log("Parsed JSON:", json);
        const rows = json.table.rows || [];
        console.log("Rows found:", rows.length);

        // Map columns: 0=date, 1=title, 2=full_text, 3=glimpse, 4=show
        const data = rows
          .map(r => ({
            date: r.c[0]?.v,           // Column 0: date
            title: r.c[1]?.v,          // Column 1: title
            fullText: r.c[2]?.v,       // Column 2: full_text
            glimpse: r.c[3]?.v,        // Column 3: glimpse
            show: r.c[4]?.v            // Column 4: show
          }))
          .filter(a => a.show === "yes" && a.date); // Only show items marked "yes"

        console.log("Filtered announcements:", data);
        setSheetAnnouncements(data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Error fetching Google Sheets data:", err);
        setIsLoading(false);
      });
  }, []);

  // Process and merge announcements
  const announcements = React.useMemo(() => {
    // Normalize legacy announcements (on or before Feb 7, 2026)
    const legacyAnnouncements = legacyAnnouncementsRaw
      .map(a => ({
        ...a,
        dateObj: new Date(a.addedTimestamp),
        source: "legacy"
      }))
      .filter(a => a.dateObj <= CUTOVER_DATE);

    console.log("Legacy announcements:", legacyAnnouncements.length);

    // Normalize sheet announcements (after Feb 7, 2026 - i.e., Feb 8 onwards)
    const sheetNormalized = sheetAnnouncements
      .map(a => {
        console.log("Processing sheet announcement:", a);
        
        // Google Sheets returns dates as "Date(2026,1,19,0,0,0)" format
        // We need to parse this properly
        let dateObj;
        if (typeof a.date === 'string' && a.date.startsWith('Date(')) {
          // Extract the date components from the Date() string
          const match = a.date.match(/Date\((\d+),(\d+),(\d+)/);
          if (match) {
            const year = parseInt(match[1]);
            const month = parseInt(match[2]); // Month is 0-indexed in JS
            const day = parseInt(match[3]);
            dateObj = new Date(year, month, day);
          } else {
            dateObj = new Date(a.date);
          }
        } else {
          dateObj = new Date(a.date);
        }
        
        console.log("Date from sheet:", a.date, "-> Parsed as:", dateObj, "-> ISO:", dateObj.toISOString());
        console.log("Cutoff date:", CUTOVER_DATE.toISOString());
        console.log("Is after cutoff?", dateObj > CUTOVER_DATE);
        
        return {
          dateTitle: a.title || dateObj.toDateString(),
          addedTimestamp: dateObj.toISOString(),
          fullText: a.fullText,
          glimpse: a.glimpse,
          dateObj: dateObj,
          source: "sheet"
        };
      })
      .filter(a => {
        const isAfterCutoff = a.dateObj > CUTOVER_DATE;
        console.log("Filtering:", a.dateTitle, "After cutoff?", isAfterCutoff);
        return isAfterCutoff;
      });

    console.log("Sheet announcements after normalization:", sheetNormalized);

    // Merge and sort by date (newest first)
    const merged = [...legacyAnnouncements, ...sheetNormalized]
      .sort((a, b) => b.dateObj - a.dateObj)
      .map((a, index) => ({ ...a, id: index }));
    
    console.log("Final merged announcements:", merged.length, merged);
    
    return merged;
  }, [sheetAnnouncements]);

  // Mark latest announcement as read
  useEffect(() => {
    const latestAnnouncementTimestamp = announcements[0]?.addedTimestamp;
    if (latestAnnouncementTimestamp) {
      localStorage.setItem('lastReadAnnouncement', latestAnnouncementTimestamp);
    }
  }, [announcements]);

  // Calculate current and last Sunday for badges
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayOfWeek = today.getDay();
  
  const currentSunday = new Date(today);
  if (dayOfWeek === 0) {
    currentSunday.setDate(today.getDate());
  } else {
    currentSunday.setDate(today.getDate() + (7 - dayOfWeek));
  }
  
  const lastWeekSunday = new Date(currentSunday);
  lastWeekSunday.setDate(currentSunday.getDate() - 7);

  function handleToggle(id) {
    const opening = openId !== id;

    if (openId && panels.current[openId]) animateHeight(panels.current[openId], false);

    if (opening) {
      setOpenId(id);
      requestAnimationFrame(() => {
        if (panels.current[id]) animateHeight(panels.current[id], true);
      });
    } else {
      if (panels.current[id]) animateHeight(panels.current[id], false);
      setOpenId(null);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <style>{`
        @media (max-width: 768px) {
          .announcement-card {
            padding: 1rem !important;
            padding-left: 3rem !important;
          }
          
          .announcement-stripe {
            left: 0.5rem !important;
            width: 0.375rem !important;
          }
          
          .announcement-title {
            font-size: 1.125rem !important;
            line-height: 1.4 !important;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          .announcement-glimpse {
            font-size: 0.875rem !important;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          .announcement-content {
            padding: 0.75rem !important;
          }
          
          .announcement-paragraph {
            font-size: 0.875rem !important;
            gap: 0.5rem !important;
          }
          
          .announcement-number {
            font-size: 0.875rem !important;
          }
          
          .priest-signature {
            font-size: 0.8rem !important;
          }
          
          .badge-text {
            font-size: 0.625rem !important;
            padding: 0.25rem 0.5rem !important;
          }
          
          .read-more-btn {
            font-size: 0.7rem !important;
            padding: 0.375rem 0.75rem !important;
            border-radius: 9999px !important;
          }
        }
        
        @media (max-width: 480px) {
          .announcement-card {
            padding: 0.75rem !important;
            padding-left: 2.5rem !important;
          }
          
          .announcement-stripe {
            left: 0.375rem !important;
            width: 0.3rem !important;
          }
          
          .announcement-title {
            font-size: 1rem !important;
          }
          
          .announcement-content {
            padding: 0.625rem !important;
          }
          
          .announcement-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 0.5rem !important;
          }
        }
      `}</style>

      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
          Parish Announcements
        </h1>
      </header>

      <div className="max-w-5xl mx-auto px-2 sm:px-6 lg:px-20 pb-12 sm:pb-16 space-y-6 sm:space-y-10">
        {announcements.map((a) => {
          const parsedDate = parseDayMonthYearFromString(a.dateTitle);
          const badge =
            parsedDate && isSameDay(parsedDate, currentSunday)
              ? "Latest"
              : parsedDate && isSameDay(parsedDate, lastWeekSunday)
              ? "Last week"
              : null;
          const isOpen = openId === a.id;

          return (
            <article key={a.id} className="relative">
              <div
                className="relative rounded-xl sm:rounded-2xl overflow-visible"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,250,244,0.98), rgba(255,255,255,0.96))",
                  border: "1px solid rgba(34,30,24,0.06)",
                  boxShadow:
                    "0 34px 60px rgba(15,23,42,0.14), 0 12px 30px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.65)"
                }}
              >
                <div
                  className="announcement-stripe absolute left-6 top-6 bottom-6 w-2.5 rounded-r-2xl"
                  style={{
                    background: "linear-gradient(180deg,#FDBA74,#F59E0B)",
                    boxShadow: "0 10px 28px rgba(249,115,22,0.14)"
                  }}
                />
                <div className="announcement-card flex items-start gap-4 sm:gap-6 p-5 pl-20 relative">
                  <div className="flex-1 min-w-0">
                    <div className="announcement-header flex flex-wrap items-center justify-between gap-3 sm:gap-4">
                      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        <h2 className="announcement-title text-xl sm:text-2xl lg:text-4xl font-serif font-extrabold text-amber-800 tracking-tight">
                          {a.dateTitle}
                        </h2>
                        {badge && (
                          <span
                            className="badge-text inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold"
                            style={{
                              background: "linear-gradient(180deg,#FDE68A,#F59E0B)",
                              color: "#5a2d00",
                              boxShadow: "0 8px 20px rgba(249,115,22,0.12)"
                            }}
                          >
                            {badge}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => handleToggle(a.id)}
                        className="read-more-btn inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-white shadow"
                        style={{
                          background: "linear-gradient(180deg,#fbbf24,#f59e0b)",
                          boxShadow: "0 4px 10px rgba(249,115,22,0.25)",
                          borderRadius: "9999px",
                          transition: "all 0.3s ease"
                        }}
                      >
                        {isOpen ? "Hide" : "Read more"}
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                        >
                          <path d="M6 9l6 6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>

                    <p className="announcement-glimpse mt-3 sm:mt-4 text-base lg:text-lg text-gray-800 font-semibold max-w-3xl leading-relaxed">
                      {a.glimpse}
                    </p>

                    <div
                      ref={(el) => (panels.current[a.id] = el)}
                      style={{
                        height: "0px",
                        overflow: "hidden",
                        opacity: "0"
                      }}
                      className="mt-3 sm:mt-4"
                    >
                      <div
                        className="announcement-content p-4 rounded-xl"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,250,240,0.98))",
                          border: "1px solid rgba(34,30,24,0.04)",
                          boxShadow: "0 18px 40px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.6)"
                        }}
                      >
                        <div className="prose prose-sm lg:prose-base max-w-none text-gray-800">
                          {a.fullText
                            .split("\n")
                            .filter((line) => line.trim() !== "")
                            .map((line, idx) => (
                              <div key={idx} className="announcement-paragraph flex items-start gap-4 mb-3">
                                <div className="flex-shrink-0">
                                  <span className="announcement-number inline-block font-semibold text-amber-600 text-lg">
                                    {idx + 1}.
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="m-0 text-gray-800 leading-relaxed" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {line.trim()}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>

                        <div className="mt-3 sm:mt-4 flex justify-end">
                          <div className="text-right">
                            <p className="priest-signature font-serif italic text-amber-700 text-base sm:text-lg relative inline-block pb-2 mr-2">
                              Parish Priest – Fr. Terence Murray
                              <span
                                aria-hidden
                                className="block absolute left-0 bottom-0 w-full h-[3px] rounded-full opacity-90"
                                style={{
                                  background:
                                    "linear-gradient(90deg, rgba(245,158,11,0.0), rgba(245,158,11,0.9), rgba(245,158,11,0.0))",
                                  transform: "translateY(6px) rotate(-2deg)"
                                }}
                              />
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}